import { memo } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { useTranslate } from '@repo/unicity'
import fieldRenderers, { renderDefaultField } from './fields'
import { commonCtxEqual, getFieldTestId, groupFieldsByRow } from './field-helpers'
import type { FormFieldConfig, FieldRenderContext, FieldRenderer, Translate } from './types'

export type { FormFieldType, FormFieldConfig, FormSectionConfig, FieldRenderContext, FieldRenderer, Translate } from './types'
export { buildInitialForm, groupFieldsByRow } from './field-helpers'

const resolveRenderer = (type: FormFieldConfig['type']): FieldRenderer => fieldRenderers[type] ?? renderDefaultField

export const renderFormField = (field: FormFieldConfig, ctx: FieldRenderContext, t: Translate = k => k) => resolveRenderer(field.type)({ field, ctx, t })

// Decides whether `FieldCell` can bail out of re-rendering. Common props
// (form value, errors, disabled, handlers) are checked via `commonCtxEqual`;
// type-specific props (e.g. `dialCode` for phone, `countryList` for country)
// are checked by the matching renderer's own `areCtxEqual`.
const ctxEqualForField = (field: FormFieldConfig, a: FieldRenderContext, b: FieldRenderContext): boolean => {
    if (!commonCtxEqual(field, a, b)) return false
    return resolveRenderer(field.type).areCtxEqual?.(a, b) ?? true
}

export const FieldCell = memo(
    function FieldCell({ field, ctx }: { field: FormFieldConfig; ctx: FieldRenderContext }) {
        const { translate } = useTranslate()
        return <>{renderFormField(field, ctx, translate)}</>
    },
    (prev, next) => prev.field === next.field && ctxEqualForField(next.field, prev.ctx, next.ctx)
)

export const renderFieldRows = (fields: FormFieldConfig[], ctx: FieldRenderContext) =>
    groupFieldsByRow(fields).map(rowFields => (
        <Stack
            key={rowFields[0]?.row}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%' }}
            data-testid={getFieldTestId(ctx, `row-${rowFields[0]?.row ?? 0}`, 'fields')}>
            {rowFields.map(field => (
                <Box key={field.key} sx={{ flex: 1, minWidth: 0 }} data-testid={getFieldTestId(ctx, field.key, 'field-cell')}>
                    <FieldCell field={field} ctx={ctx} />
                </Box>
            ))}
        </Stack>
    ))
